import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  BookOpenCheck,
  BrainCircuit,
  CalendarCheck2,
  Lightbulb,
  PlusCircle,
  Target,
} from 'lucide-react';
import SEO from '@/components/SEO';
import ActiveProjectCard from '@/components/ActiveProjectCard';
import RecentSkillBadges from '@/components/RecentSkillBadges';
import TransitionTimeline from '@/components/TransitionTimeline';
import AgentQuickAccess from '@/components/AgentQuickAccess';
import SITREPCard from '@/components/SITREPCard';
import { useOnboardingGuard } from '@/hooks/use-onboarding-guard';
import { getCurrentUserProfile } from '@/lib/data-access/user-profile';
import { getUserTrackSelection } from '@/lib/data-access/user-track';

const quickStats = {
  daysUntilETS: 180, // TODO: Calculate from user profile
  skillsAcquired: 7,
  activeProjects: 2,
  readinessPercentage: 45
};

export default function Home() {
  const { isMobile } = useSidebar();
  const onboardingStatus = useOnboardingGuard();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [trackSelection, setTrackSelection] = useState<any>(null);
  const [isFirstTime, setIsFirstTime] = useState(false);

  useEffect(() => {
    async function loadUserData() {
      try {
        const profile = await getCurrentUserProfile();
        const track = await getUserTrackSelection();
        setUserProfile(profile);
        setTrackSelection(track);
        
        // Check if this is first time after onboarding (created within last 5 minutes)
        if (profile?.created_at) {
          const createdAt = new Date(profile.created_at);
          const now = new Date();
          const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
          setIsFirstTime(diffMinutes < 5);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    }

    if (onboardingStatus.isComplete) {
      loadUserData();
    }
  }, [onboardingStatus.isComplete]);

  if (onboardingStatus.isLoading) {
    return null; // Guard will redirect if needed
  }

  const userName = userProfile?.first_name || 'Service Member';

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6 pb-12 md:pb-16">
      <SEO />
      
      {/* First-time welcome message */}
      {isFirstTime && trackSelection && (
        <Card accent className="bg-primary/5 border-primary/30 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-primary">
                  Welcome, {userName}!
                </h3>
                <p className="text-sm text-foreground mt-2">
                  You're targeting <strong className="text-primary">{trackSelection.track?.name}</strong> over the next{' '}
                  <strong className="text-primary">{trackSelection.target_time_horizon_weeks} weeks</strong>.
                  Your personalized learning plan is ready below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b-2 border-primary/20 pb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">Transition Overview</h1>
          <p className="text-lg font-medium text-secondary mt-2">
            Mission: {trackSelection?.track?.name || 'Software Engineering Career'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Track your progress, build your portfolio, and prepare for deployment.
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          {isMobile && <SidebarTrigger />}
        </div>
      </div>

      {/* Mission Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card shadow-md border-border hover:shadow-xl hover:border-primary/50 transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-card-foreground">Days Until ETS</CardTitle>
            <div className="p-1.5 rounded-md bg-primary/10">
              <CalendarCheck2 className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{quickStats.daysUntilETS}</div>
            <p className="text-xs text-muted-foreground mt-1">Time to job-ready target</p>
          </CardContent>
        </Card>
        <Card className="bg-card shadow-md border-border hover:shadow-xl hover:border-primary/50 transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-card-foreground">Skills Acquired</CardTitle>
            <div className="p-1.5 rounded-md bg-primary/10">
              <BookOpenCheck className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{quickStats.skillsAcquired}/15</div>
            <p className="text-xs text-muted-foreground mt-1">Core technical skills</p>
          </CardContent>
        </Card>
        <Card className="bg-card shadow-md border-border hover:shadow-xl hover:border-primary/50 transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-card-foreground">Active Projects</CardTitle>
            <div className="p-1.5 rounded-md bg-primary/10">
              <Target className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{quickStats.activeProjects}</div>
            <p className="text-xs text-muted-foreground mt-1">Portfolio in progress</p>
          </CardContent>
        </Card>
        <Card className="bg-card shadow-md border-border hover:shadow-xl hover:border-primary/50 transition-all duration-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-card-foreground">Mission Readiness</CardTitle>
            <div className="p-1.5 rounded-md bg-primary/10">
              <Lightbulb className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{quickStats.readinessPercentage}%</div>
            <p className="text-xs text-muted-foreground mt-1">Job-ready status</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" size="sm" className="hover:bg-primary/10 hover:border-primary hover:text-primary transition-colors" asChild>
          <Link to="/skill-map">
            <Target className="mr-2 h-4 w-4" /> View Skill Map
          </Link>
        </Button>
        <Button variant="outline" size="sm" className="hover:bg-secondary/10 hover:border-secondary hover:text-secondary transition-colors" asChild>
          <Link to="/learning-plan">
            <BookOpenCheck className="mr-2 h-4 w-4" /> Continue Learning
          </Link>
        </Button>
        <Button variant="outline" size="sm" className="hover:bg-secondary/10 hover:border-secondary hover:text-secondary transition-colors" asChild>
          <Link to="/projects">
            <PlusCircle className="mr-2 h-4 w-4" /> Start New Project
          </Link>
        </Button>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Objectives */}
        <Card accent className="bg-card shadow-md border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-primary flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-primary/10">
                <BrainCircuit className="h-5 w-5 text-primary" />
              </div>
              Weekly Objectives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-primary/5 hover:border hover:border-primary/20 transition-all cursor-pointer">
                <div className="mt-0.5">
                  <div className="h-4 w-4 rounded border-2 border-primary hover:bg-primary/10 transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Complete JavaScript Fundamentals Module 3</p>
                  <p className="text-xs text-muted-foreground">Estimated: 4 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-primary/5 hover:border hover:border-primary/20 transition-all cursor-pointer">
                <div className="mt-0.5">
                  <div className="h-4 w-4 rounded border-2 border-primary hover:bg-primary/10 transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Start Portfolio Project: Task Manager</p>
                  <p className="text-xs text-muted-foreground">Estimated: 6 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-primary/5 hover:border hover:border-primary/20 transition-all cursor-pointer">
                <div className="mt-0.5">
                  <div className="h-4 w-4 rounded border-2 border-primary hover:bg-primary/10 transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Update resume with new skills</p>
                  <p className="text-xs text-muted-foreground">Estimated: 1 hour</p>
                </div>
              </div>
            </div>
            <Button variant="link" className="mt-4 p-0 text-primary hover:text-primary/80" asChild>
              <Link to="/learning-plan">
                View Full Learning Plan <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Active Project Status */}
        <ActiveProjectCard />

        {/* Transition Timeline */}
        <TransitionTimeline />

        {/* AI Agent Quick Access */}
        <AgentQuickAccess />
      </div>

      {/* Recent Skills Section */}
      <RecentSkillBadges />

      {/* SITREP Summary */}
      <SITREPCard />
    </div>
  );
}
